<script setup>
import { ref, computed } from 'vue';
import { useFieldsStore } from '@/stores/storeFieldData';
import { useRequestsStore } from '@/stores/storeRequests';

const storeField = useFieldsStore();
const storeRequests = useRequestsStore();

const MAX_FILE_SIZE = 5 * 1024 * 1024;
const MAX_FILES_COUNT = 10;

const filesCount = computed(() => storeField.fields.files.length);
const isMaxFilesReached = computed(() => filesCount.value >= MAX_FILES_COUNT);

const handleFiles = (e) => {
    const files = Array.from(e.target.files);
    let fileId = storeField.fields.files.length > 0 
        ? Math.max(...storeField.fields.files.map(f => f.id)) 
        : 0;
    
    // Фильтруем только те файлы, которые можно добавить (не превышая лимит)
    const filesToAdd = files.slice(0, MAX_FILES_COUNT - filesCount.value);
    
    if (filesToAdd.length === 0) {
        // alert(`Максимальное количество файлов (${MAX_FILES_COUNT}) уже достигнуто.`);
        storeRequests.responseError = `Максимальное количество файлов (${MAX_FILES_COUNT}) уже достигнуто.`
        return;
    }

    for(let file of filesToAdd) {
        if(file.size <= MAX_FILE_SIZE) {
            fileId++;
            storeField.fields.files.push({
                id: fileId,
                nameFile: file.name,
                sizeFile: file.size,
                imgFile: window.URL.createObjectURL(file),
            });
            storeField.fields.filesForPost.push(file);
        } else {
            // alert(`Файл "${file.name}" превышает 5MB и не был добавлен.`);
            storeRequests.responseError = `Файл "${file.name}" превышает 5MB и не был добавлен.`
        }
    }

    // Если попытались добавить больше файлов чем доступно
    if (files.length > filesToAdd.length) {
        // alert(`Добавлено ${filesToAdd.length} из ${files.length} файлов. Максимум ${MAX_FILES_COUNT} файлов.`);
        storeRequests.responseError = `Добавлено ${filesToAdd.length} из ${files.length} файлов. Максимум ${MAX_FILES_COUNT} файлов.`
    }
}

const deleteFile = (name) => {
    storeField.fields.files = storeField.fields.files.filter(file => file.nameFile !== name);
    storeField.fields.filesForPost = storeField.fields.filesForPost.filter(file => file.name !== name);
}

const returnFileSize = (number) => {
    if (number < 1024) {
        return number + "bytes";
    } else if (number > 1024 && number < 1048576) {
        return (number / 1024).toFixed(1) + "KB";
    } else if (number > 1048576) {
        return (number / 1048576).toFixed(1) + "MB";
    }
}

</script>
<template>
    <label :class="['file', { '--disabled': isMaxFilesReached }]">
        <input 
            type="file" 
            class="file__real"
            accept=".jpg, .jepg, .png, .gif, .tiff, .webp, .pdf"
            multiple
            @change="handleFiles"
            :disabled="isMaxFilesReached"
        >
        <span class="file__custom"></span>
        <span class="file__text" v-if="!isMaxFilesReached">Прикрепить файл</span>
        <span class="file__text" v-else>Максимум файлов достигнут</span>
    </label>
    
    <ul class="file-desc__list">
        <li class="file-desc__item">Разрешенные форматы файлов: .jpg, .jpeg, .png, .gif, .tiff, .webp, .pdf.</li>
        <li class="file-desc__item">Максимальный размер файла: 5 MB.</li>
        <li class="file-desc__item">Максимальное количество файлов: 10 (добавлено: {{ filesCount }}/10).</li>
    </ul>
    <div class="preview-file">
        <TransitionGroup name="list" tag="ul" class="preview-file__list">
            <li class="preview-file__item" v-for="{file, id, imgFile, nameFile} in storeField.fields.files" :key="id">
                
                <img class='preview-file__file-image' width='40' height='40' :src='imgFile' alt='изображение'>
                <button class="preview-file__btn" type="button" @click="deleteFile(nameFile)">
                    <svg width='20' height='20'><use href='#x'></use></svg>
                </button>
            </li>
        </TransitionGroup>
    </div>
</template>
<style lang='scss' scoped>
@use '@/assets/scss/base/vars';
.file {
    width: fit-content;    
    display: flex;
    align-items: center;
    gap: .8rem;
    margin-left: 10px;
    @media (hover: hover){
        &:hover {
            .file__text {

                text-decoration: underline;
            }
        }
    }
}
.file__real {
    width: 0;
    height: 0;
    opacity: 0;
    position: absolute;
    z-index: -1;
}
.file__custom {
   background-image: url('@/assets/img/icons/paperclip.svg');
   background-repeat: no-repeat;
   background-position: 0px 0px; /* x y*/
   background-size: auto; /* w h*/
   width: 20px;
   height: 20px;
}

.file__text {
    font-weight: 500;
    font-size: 1.6rem;
    line-height: 160%;
    color: var(--text-default-100);

}
.file-desc__list {
    margin-bottom: 20px;
}
.file-desc__item {
    font-weight: 700;
    font-size: 1.4rem;
    line-height: 160%;
    color: var(--text-default-100);
}
.preview-file {  
}
.preview-file__list {
 display: grid;
 grid-template-columns: repeat(auto-fill, minmax(10rem, 1fr));
 gap: .8rem;
 @media (max-width: vars.$x480) {
    grid-template-columns: repeat(auto-fill, minmax(6rem, 1fr));
 }
}
.preview-file__item {
    position: relative; 
    overflow: hidden;
    border: 2px solid rgba(197, 198, 202, 0.24);
    border-radius: 0.8rem;
    width: 100%;
    aspect-ratio: 1/1;
}

.preview-file__file-image {
    width: 100%;
    height: 100%;
    object-fit: cover;
}
.preview-file__btn {
    position: absolute;
    top: 8px;
    right: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 0.2rem;
    width: 2.4rem;
    height: 2.4rem;
    background: var(--fill-default-1);
    color: var(--text-accent-1-100);
    svg {
        width: 2rem;
        height: 2rem;
        stroke-width: 2px;
        fill: transparent;
    }
}

.list-enter-active,
.list-leave-active {
    transition: all 0.5s ease;
}
.list-enter-from,
.list-leave-to {
    opacity: 0;
    transform: translateX(30px);
}
</style>